import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { businessSlice } from "../../../../../../features/business/businessSlice";
import ProtectedPaymentRoute from "../../../../../auth/Auth";
import { useConfig } from "../../../../../../app/configcontext";

export const validateAlphabets = (text) => {
  const regex = /^[A-Za-z\s]+$/;
  return regex.test(text);
};

export const validateNumber = (text) => {
  const regex = /^[0-9]+$/;
  return regex.test(text);
};

const BusinessLocation = () => {
  ProtectedPaymentRoute();

  const config = useConfig();
  const states = config?.states || [];

  const actions = businessSlice.actions;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const savedBusinessLocation = useSelector(
    (state) => state.business.businessLocation
  );

  const [businessLocation, setBusinessLocation] = useState({
    street: savedBusinessLocation.street || "",
    city: savedBusinessLocation.city || "",
    stateCode: savedBusinessLocation.state || "",
    zipCode: savedBusinessLocation.zipcode || "",
  });

  const [errors, setErrors] = useState({
    street: "",
    city: "",
    stateCode: "",
    zipCode: "",
  });

  const handleInputChange = (field, value) => {
    let isValid = true;
    let errorMsg = "";

    if (field === "street") {
      if (value.replace(/[^0-9]/g, "").length > 10) {
        isValid = false;
        errorMsg = "Street number/name cannot have more than 10 digits.";
      }
    } else if (field === "city" || field === "stateCode") {
      if (!validateAlphabets(value)) {
        isValid = false;
        errorMsg = `${field.charAt(0).toUpperCase() + field.slice(1)} should only contain alphabets.`;
      }
    } else if (field === "zipCode") {
      if (!validateNumber(value)) {
        isValid = false;
        errorMsg = "Zip code should only contain numbers.";
      } else if (value.length > 5) {
        value = value.slice(0, 5);
      } else if (value.length < 5) {
        isValid = false;
        errorMsg = "Zip code must be exactly 5 digits.";
      }
    }

    if (!isValid) {
      setErrors((prev) => ({ ...prev, [field]: errorMsg }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    setBusinessLocation((prev) => ({ ...prev, [field]: value }));
  };

  const validateInput = (field) => {
    const value = (businessLocation[field] || "").trim();
    let isValid = value !== "";

    if (!isValid) {
      setErrors((prev) => ({ ...prev, [field]: `Please enter ${field}.` }));
    } else {
      if (field === "zipCode") {
        isValid = isValid && validateNumber(value) && value.length === 5;
        if (!isValid) {
          setErrors((prev) => ({ ...prev, [field]: "Please enter a valid 5-digit ZIP code." }));
        } else {
          setErrors((prev) => ({ ...prev, [field]: "" }));
        }
      } else if (field === "city" || field === "stateCode") {
        isValid = isValid && validateAlphabets(value);
        if (!isValid) {
          setErrors((prev) => ({ ...prev, [field]: `Please enter a valid ${field}.` }));
        } else {
          setErrors((prev) => ({ ...prev, [field]: "" }));
        }
      } else {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }

    return isValid;
  };

  const handleNextButtonClick = (event) => {
    event.preventDefault();
    const isStreetValid = validateInput("street");
    const isCityValid = validateInput("city");
    const isStateCodeValid = validateInput("stateCode");
    const isZipCodeValid = validateInput("zipCode");

    if (isStreetValid && isCityValid && isStateCodeValid && isZipCodeValid) {
      dispatch(
        actions.update_business_location({
          street: businessLocation.street,
          city: businessLocation.city,
          state: businessLocation.stateCode,
          zipcode: businessLocation.zipCode,
        })
      );

      const searchParams = new URLSearchParams(location.search);
      const editParam = searchParams.get("edit");
      if (editParam === "true") {
        navigate("/invoice");
      } else {
        navigate("/name_confirm");
      }
    }
  };

  const handleNextPress = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNextButtonClick(event);
    }
  };

  const backbtn = () => {
    navigate("/business_name");
  };

  const isEdit = new URLSearchParams(location.search).get("edit") === "true";

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans flex items-center">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Graphic */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <img 
              src="/Assets/Form_img/building.png" 
              alt="Business Location" 
              className="w-full max-w-[340px] h-auto object-contain"
            />
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                Step 2 of Business Formation
              </span>
              <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
                Where is your business located?
              </h1>
              <p className="text-[15px] text-[#5f6368] leading-relaxed">
                Provide the physical street address and state of formation for your entity.
              </p>
            </div>

            <form onKeyDown={handleNextPress} className="space-y-5 max-w-xl">
              {/* Street */}
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  Street Address <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 123 Enterprise Blvd, Suite 400"
                  value={businessLocation.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                  onBlur={() => validateInput("street")}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.street ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.street && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.street}
                  </p>
                )}
              </div>

              {/* City & State Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                    City <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. San Francisco"
                    value={businessLocation.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    onBlur={() => validateInput("city")}
                    className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                      errors.city ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                    } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                  />
                  {errors.city && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.city}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                    State <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <Select
                    showSearch
                    size="large"
                    placeholder="Select State"
                    className="w-full h-[48px]"
                    value={businessLocation.stateCode || undefined}
                    onChange={(value) => handleInputChange("stateCode", value)}
                    onBlur={() => validateInput("stateCode")}
                    status={errors.stateCode ? "error" : ""}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={states.map(s => ({ value: s.name, label: s.name }))}
                  />
                  {errors.stateCode && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.stateCode}
                    </p>
                  )}
                </div>
              </div>

              {/* Zip Code */}
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  ZIP Code <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 94105"
                  maxLength={5}
                  value={businessLocation.zipCode}
                  onChange={(e) => handleInputChange("zipCode", e.target.value)}
                  onBlur={() => validateInput("zipCode")}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.zipCode ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.zipCode && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.zipCode}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-6">
                {!isEdit && (
                  <button
                    type="button"
                    onClick={backbtn}
                    className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
                  >
                    <i className="fa-solid fa-arrow-left text-xs"></i> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNextButtonClick}
                  className="px-8 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  {isEdit ? "Update Location" : "Next"} <i className="fa-solid fa-arrow-right text-xs"></i>
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BusinessLocation;

